import { useLocation } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';

const LOADING_LABEL = 'Loading integration…';

/** Both spellings of one connector's URL, or `null` while the workspace id resolves. */
type ConnectorHandoffHrefs = {
  migrated: string | null;
  legacy: string | null;
};

type ConnectorHrefBuilders = {
  /** Builds the migrated URL — the one without the `journeys` segment. */
  buildMigrated: (workspaceId: string) => string;
  /** Builds the `journeys` twin, which the embedded Ember app serves. */
  buildLegacy: (workspaceId: string) => string;
};

/**
 * The migrated and legacy URLs for one connector, with the current query string
 * and hash carried onto both.
 *
 * Callers pass builders rather than route templates so each keeps its own typed
 * route id — `buildLocation` will not take a bare string.
 *
 * Returns `null` until the workspace id is readable: `/workspaces/last` is
 * rewritten by a bridge-synced navigation that leaves route params stale, so the
 * id comes off the reactive location and callers hold a loader until it lands.
 */
export function useConnectorHandoffHrefs({ buildMigrated, buildLegacy }: ConnectorHrefBuilders): ConnectorHandoffHrefs {
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return { migrated: null, legacy: null };
  }

  // Ember transitions preserve query params, and some connector pages deep-link
  // by fragment, so both directions carry the query string and the hash.
  const suffix = `${searchStr}${hash === '' ? '' : `#${hash}`}`;

  return {
    migrated: `${buildMigrated(workspaceId)}${suffix}`,
    legacy: `${buildLegacy(workspaceId)}${suffix}`,
  };
}

type JourneysConnectorHandoffProps = {
  isMigrated: boolean;
  /** The migrated URL to forward to, from {@link useConnectorHandoffHrefs}. */
  migratedHref: string | null;
};

/**
 * The `journeys`-spelled URL of a migrated connector.
 *
 * Flag off → the embedded Ember app still owns the page and renders here. Flag on
 * → Hydra has claimed this URL only to forward it, so the integrations directory
 * and any other Ember-side link keep working without being re-pointed.
 */
export function JourneysConnectorHandoff({ isMigrated, migratedHref }: JourneysConnectorHandoffProps) {
  if (!isMigrated) {
    return <Journeys />;
  }

  if (migratedHref == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={migratedHref} options={{ shouldReplaceHistoryEntry: true }} />;
}
